import React, { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SEOHead } from '../components/ui/SEOHead'
import { SearchBar } from '../components/search/SearchBar'
import { SearchResults } from '../components/search/SearchResults'
import { Pagination } from '../components/ui/Pagination'
import { AsciiDivider } from '../components/terminal/AsciiDivider'
import { useSearch } from '../hooks/useSearch'

export const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const urlQuery = searchParams.get('q') ?? ''

  const { results, total, totalPages, page, loading, query, setQuery, setPage } =
    useSearch(urlQuery)

  // Sync URL → hook when URL changes externally
  useEffect(() => {
    setQuery(urlQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery])

  const handleSearch = (q: string) => {
    setQuery(q)
    const params = new URLSearchParams(searchParams)
    if (q.trim()) {
      params.set('q', q.trim())
    } else {
      params.delete('q')
    }
    setSearchParams(params, { replace: true })
  }

  return (
    <>
      <SEOHead
        title={query ? `Search: ${query}` : 'Search'}
        description="Search CodeToByte posts"
        url={`/search${query ? `?q=${encodeURIComponent(query)}` : ''}`}
      />

      <div
        style={{
          paddingTop: '32px',
          fontFamily: "'JetBrains Mono', 'Courier New', monospace",
        }}
      >
        <div
          style={{
            fontSize: '1.1rem',
            color: 'var(--accent-green)',
            marginBottom: '20px',
          }}
        >
          &gt;_ search
        </div>

        <div style={{ maxWidth: '560px', marginBottom: '8px' }}>
          <SearchBar
            onSearch={handleSearch}
            initialValue={urlQuery}
            placeholder="search posts, tags, categories..."
          />
        </div>
        <div
          style={{
            fontSize: '0.72rem',
            color: 'var(--text-muted, #555)',
            marginBottom: '24px',
          }}
        >
          Press <kbd style={{ border: '1px solid var(--border-color)', padding: '1px 4px', borderRadius: '2px' }}>/</kbd> anywhere to focus search
        </div>

        <AsciiDivider width={60} />

        <SearchResults
          results={results}
          total={total}
          query={query}
          loading={loading}
        />

        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={(p) => {
              setPage(p)
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }}
          />
        )}
      </div>
    </>
  )
}
